import { Help } from '@carbon/react/icons';
import {
  DataTable,
  DataTableRenderProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { SkeletonTableRows } from 'components/skeleton-table-rows/skeleton-table-rows';
import useClickOutside from 'lib/hooks/useClickOutside';
import { useMemo, useRef, useState } from 'react';
import { StructureGridCollectionValidityResponse } from 'types/api-responses';
import styles from './structure-grid-collection-validity.module.scss';

interface StructureGridCollectionValidityProps {
  validityResults?: StructureGridCollectionValidityResponse;
  loading: boolean;
}

export const StructureGridCollectionValidity = ({
  validityResults,
  loading,
}: StructureGridCollectionValidityProps) => {
  const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);
  const tooltipRefs = useRef<HTMLDivElement[]>([]);

  // Close tooltip when clicking outside
  useClickOutside({
    refs: tooltipRefs.current.map((ref) => ({ current: ref })),
    onClickOutside: () => setOpenTooltipId(null),
  });

  // Map API data to validation checks
  const mappedValidationChecks = useMemo(() => {
    if (
      !validityResults ||
      !validityResults.results ||
      validityResults.results.length === 0
    ) {
      return [];
    }

    return validityResults.results.map((result, index) => {
      const { displayMetadata, status: resultStatus } = result;
      const { helpHint, failureMessages, status } = displayMetadata;

      // Create tooltip content from helpHint
      const tooltipText = helpHint ? (
        <div className={styles.tooltipContent}>
          <div className={styles.tooltipTitle}>{helpHint.label}</div>
          <div className={styles.tooltipLevel}>
            Level:{' '}
            <span
              className={styles.preserveWhitespace}
              dangerouslySetInnerHTML={{
                __html: helpHint.level
                  .replace(
                    /\bError\b/g,
                    '<span class="error-text">Error</span>'
                  )
                  .replace(
                    /\bWarning\b/g,
                    '<span class="warning-text">Warning</span>'
                  ),
              }}
            />
          </div>
          <div className={styles.tooltipDescription}>
            {helpHint.description}
          </div>
          <div>
            <strong>Failure indicates:</strong> {helpHint.failureIndication}
          </div>
        </div>
      ) : null;

      // Format failure messages - join array elements with line breaks
      const message =
        failureMessages && failureMessages.length > 0
          ? failureMessages.join('\n')
          : '--';

      // If status is not 'processed', show -- for both result and message
      const isProcessed = resultStatus === 'processed';
      const displayResult = isProcessed ? status || '--' : '--';
      const displayMessage = isProcessed ? message : '--';

      return {
        id: `${result.type}-${index}`,
        validationCheck: displayMetadata.label,
        result: displayResult,
        message: displayMessage,
        tooltipText: tooltipText,
        level: result.level,
      };
    });
  }, [validityResults]);

  const validationChecksData = mappedValidationChecks;

  return (
    <div className={styles.validityTableArea}>
      <DataTable
        headers={[
          { key: 'validationCheck', header: 'Validation Check' },
          { key: 'result', header: 'Result' },
          { key: 'message', header: 'Message' },
        ]}
        rows={validationChecksData}
      >
        {({
          rows,
          headers,
          getTableProps,
          getHeaderProps,
          getRowProps,
        }: DataTableRenderProps<any, any[]>) => (
          <Table {...getTableProps()} className={styles.validityTable}>
            <TableHead>
              <TableRow>
                {headers.map((header: any) => (
                  <TableHeader
                    {...getHeaderProps({ header })}
                    key={header.key}
                    className={
                      header.key === 'validationCheck'
                        ? styles.validationCheckColumn
                        : header.key === 'result'
                          ? styles.resultColumn
                          : styles.messageColumn
                    }
                  >
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <SkeletonTableRows columns={3} rows={6} />
              ) : (
                rows.map((row: any) => {
                  const rowData = validationChecksData.find(
                    (item) => item.id === row.id
                  );
                  return (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      {row.cells.map((cell: any) => (
                        <TableCell
                          key={cell.id}
                          className={
                            cell.info.header === 'validationCheck'
                              ? styles.validationCheckColumn
                              : cell.info.header === 'result'
                                ? styles.resultColumn
                                : styles.messageColumn
                          }
                        >
                          {cell.info.header === 'validationCheck' &&
                          rowData?.tooltipText ? (
                            <div className={styles.validationCheckWithTooltip}>
                              <span>
                                {cell.value}
                                <div
                                  ref={(el) => {
                                    if (el) {
                                      tooltipRefs.current[
                                        parseInt(rowData.id.split('-')[1])
                                      ] = el;
                                    }
                                  }}
                                  className={styles.tooltipTrigger}
                                  onClick={() =>
                                    setOpenTooltipId(
                                      openTooltipId === rowData.id
                                        ? null
                                        : rowData.id
                                    )
                                  }
                                >
                                  <Help />
                                  {openTooltipId === rowData.id && (
                                    <div className={styles.customTooltip}>
                                      {rowData.tooltipText}
                                    </div>
                                  )}
                                </div>
                              </span>
                            </div>
                          ) : cell.info.header === 'result' ? (
                            <span
                              className={
                                rowData?.result === 'Failed'
                                  ? styles.errorText
                                  : rowData?.result === 'Warning'
                                    ? styles.warningText
                                    : ''
                              }
                            >
                              {cell.value}
                            </span>
                          ) : cell.info.header === 'message' ? (
                            <div
                              className={`${styles.messageText} ${
                                cell.value !== '--' &&
                                rowData?.result === 'Failed'
                                  ? styles.errorText
                                  : cell.value !== '--' &&
                                      rowData?.result === 'Warning'
                                    ? styles.warningText
                                    : ''
                              }`}
                            >
                              {cell.value}
                            </div>
                          ) : (
                            cell.value
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </DataTable>
    </div>
  );
};
